import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CreditCard, Gift, Ticket, Landmark, User2, Shield, Bell, Plus, Download } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Toranam } from "@/components/Ornaments";
import PaymentModal from "@/components/PaymentModal";
import MyTemples, { PERM_MSG } from "@/components/MyTemples";
import { toast } from "sonner";

export default function Dashboard() {
  const { t, settings } = useApp();
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [txns, setTxns] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [archaka, setArchaka] = useState(null);
  const [pay, setPay] = useState(false);
  const [pkg, setPkg] = useState("standard");

  const downloadReceipt = async (txnId) => {
    try {
      const res = await api.get(`/payments/receipt/${txnId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${txnId}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const loadActivity = () => {
    api.get("/payments/transactions").then(({ data }) => setTxns(data)).catch(() => {});
    api.get("/notifications").then(({ data }) => setNotifs(data.items)).catch(() => {});
  };

  useEffect(() => {
    loadActivity();
    if (user?.role === "archaka") api.get("/me/archaka").then(({ data }) => setArchaka(data)).catch(() => {});
  }, [user]);

  const deleteProfile = async () => {
    if (!window.confirm(PERM_MSG)) return;
    try { await api.delete("/me/archaka"); toast.success("ప్రొఫైల్ శాశ్వతంగా తొలగించబడింది"); setArchaka({}); refreshUser(); }
    catch (e) { toast.error(e?.response?.data?.detail || "లోపం"); }
  };

  if (!user) return null;
  const subActive = !!user.subscription_until && new Date(user.subscription_until) > new Date();
  const usedCredits = 5 - (user.credits > 5 ? 5 : user.credits);

  return (
    <div className="parchment-texture min-h-screen">
      <div className="bg-gradient-to-r from-maroon to-saffron py-8">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white">🙏 నమస్తే, {user.name}</h1>
            <p className="text-marigold font-telugu">{user.unique_id} · {user.role === "temple" ? t("role_temple") : user.role === "archaka" ? t("role_archaka") : "Admin"}</p>
          </div>
          {user.role === "admin" && <Button data-testid="dash-admin-link" onClick={() => nav("/admin")} className="bg-white text-maroon hover:bg-white/90 font-telugu"><Shield className="w-4 h-4 mr-2" />{t("admin_panel")}</Button>}
        </div>
      </div>
      <Toranam />

      <div className="max-w-5xl mx-auto px-6 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {user.role === "archaka" && (
            <Card className="p-6 bg-surface gold-border temple-shadow" data-testid="dash-subscription">
              <h3 className="font-serifte font-bold text-maroon text-lg flex items-center gap-2 mb-2"><Gift className="w-5 h-5 text-saffron" />అర్చక నెలవారీ సభ్యత్వం</h3>
              {subActive ? (
                <p className="text-sm font-telugu text-green-700" data-testid="dash-sub-active">✅ సక్రియం · {new Date(user.subscription_until).toLocaleDateString("te-IN")} వరకు అన్ని దేవాలయాల కాంటాక్ట్ కనిపిస్తాయి</p>
              ) : (
                <p className="text-sm font-telugu text-muted-foreground" data-testid="dash-sub-inactive">దేవాలయాల ఫోన్ నంబర్, చిరునామా చూడాలంటే ₹{settings?.archaka_monthly_fee || 499} చెల్లించండి. 30 రోజులు పనిచేస్తుంది.</p>
              )}
              <Button data-testid="dash-buy-subscription" onClick={() => setPay(true)} className="mt-3 btn-sacred border-0 font-telugu"><Plus className="w-4 h-4 mr-1" />{subActive ? "మరో 30 రోజులు పొడిగించండి" : "సభ్యత్వం తీసుకోండి"} (₹{settings?.archaka_monthly_fee || 499})</Button>
            </Card>
          )}

          {user.role === "temple" && <MyTemples />}

          {/* Archaka: profile */}
          {user.role === "archaka" && (
            <Card className="p-6 bg-surface gold-border temple-shadow">
              <h3 className="font-serifte font-bold text-maroon text-lg flex items-center gap-2 mb-3"><User2 className="w-5 h-5 text-saffron" />నా ప్రొఫైల్</h3>
              {archaka?.id ? (
                <div className="space-y-2">
                  <p className="font-telugu text-maroon">స్థితి: <Badge className={archaka.status === "approved" ? "bg-green-100 text-green-800" : archaka.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>{t(archaka.status)}</Badge></p>
                  <p className="text-sm font-telugu text-muted-foreground">🪷 గోత్రం: {archaka.gotram} {archaka.shakha && `| శాఖ: ${archaka.shakha}`}</p>
                  {archaka.status === "rejected" && archaka.reject_reason && <p className="text-sm text-red-700 font-telugu">కారణం: {archaka.reject_reason}</p>}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button data-testid="dash-edit-profile" onClick={() => nav("/archaka-register")} className="btn-sacred border-0 font-telugu">ప్రొఫైల్ సవరించండి</Button>
                    <Button data-testid="dash-delete-profile" variant="outline" onClick={deleteProfile} className="text-destructive border-red-200 font-telugu">ప్రొఫైల్ తొలగించండి</Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground font-telugu">ఉద్యోగం దొరికిన తర్వాత ప్రొఫైల్‌ను తొలగించవచ్చు.</p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="font-telugu text-muted-foreground mb-3">మీరు ఇంకా ప్రొఫైల్ సృష్టించలేదు</p>
                  <Button data-testid="dash-create-profile" onClick={() => nav("/archaka-register")} className="btn-sacred border-0 font-telugu">ప్రొఫైల్ సృష్టించండి</Button>
                </div>
              )}
            </Card>
          )}

          {/* Transactions */}
          <Card className="p-6 bg-surface gold-border temple-shadow">
            <h3 className="font-serifte font-bold text-maroon text-lg flex items-center gap-2 mb-3"><Ticket className="w-5 h-5 text-saffron" />చెల్లింపు రసీదులు</h3>
            {txns.length === 0 ? <p className="text-sm text-muted-foreground font-telugu">చెల్లింపులు లేవు</p> : (
              <div className="space-y-2" data-testid="dash-txns">
                {txns.map((tx) => (
                  <div key={tx.txn_id} className="flex items-center justify-between p-3 rounded-lg border border-goldline/30 text-sm">
                    <div><div className="font-medium text-maroon">₹{tx.amount} · {{ archaka_subscription: "అర్చక నెలవారీ సభ్యత్వం", archaka_unlock: "అర్చక కాంటాక్ట్ అన్‌లాక్", temple_unlock: "దేవాలయ కాంటాక్ట్ అన్‌లాక్", credit_pack: "క్రెడిట్ ప్యాక్" }[tx.item_type] || tx.item_type}</div><div className="text-xs text-muted-foreground">{tx.txn_id}</div></div>
                    <div className="flex items-center gap-2">
                      <Badge data-testid={`txn-status-${tx.txn_id}`} className={tx.payment_status === "paid" ? "bg-green-100 text-green-800" : tx.payment_status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>{tx.payment_status === "paid" ? "చెల్లించారు" : tx.payment_status === "rejected" ? "తిరస్కరించారు" : "పరిశీలనలో"}</Badge>
                      {tx.payment_status === "paid" && (
                        <Button size="sm" variant="outline" data-testid={`receipt-${tx.txn_id}`} onClick={() => downloadReceipt(tx.txn_id)} className="h-8 border-goldline/50 text-saffron">
                          <Download className="w-3.5 h-3.5 mr-1" />రసీదు
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Notifications sidebar */}
        <div className="lg:col-span-1">
          <Card className="p-5 bg-surface gold-border temple-shadow">
            <h3 className="font-serifte font-bold text-maroon text-lg flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-saffron" />{t("notifications")}</h3>
            <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="dash-notifs">
              {notifs.length === 0 ? <p className="text-sm text-muted-foreground font-telugu">{t("no_notifications")}</p> :
                notifs.slice(0, 15).map((n, i) => (
                  <div key={i} className={`p-2.5 rounded-lg border-l-2 ${!n.read ? "border-saffron bg-marigold/10" : "border-goldline/40"}`}>
                    <p className="text-sm font-semibold text-maroon font-telugu">{n.title}</p>
                    <p className="text-xs text-muted-foreground font-telugu">{n.body}</p>
                  </div>
                ))}
            </div>
          </Card>
        </div>
      </div>

      <PaymentModal open={pay} onClose={() => setPay(false)} itemType="archaka_subscription" onSuccess={() => { refreshUser(); loadActivity(); }} />
    </div>
  );
}
